'use client'

import { useState } from 'react'
import Image from 'next/image'
import FadeUp from '@/components/ui/FadeUp'
import BackgroundLayers from '@/components/ui/BackgroundLayers'
import { getStrapiMedia } from '@/lib/strapi/client'
import type { TeaserImageGridBlockType } from '@/lib/types/teaser'
import TeaserImageGridCarousel from './TeaserImageGridCarousel'
import TeaserImageGridLightbox from './TeaserImageGridLightbox'

type Props = {
  data: TeaserImageGridBlockType
}

/** Desktop mosaic: featured 2×2 top-left, two stacked right, three across bottom.
 *  Mobile: full → pair → pair → full (1-2-2-1). */
function getMosaicItemClassName(index: number): string {
  const base =
    'relative overflow-hidden rounded bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)]'

  if (index === 0) {
    return `${base} col-span-2 aspect-[358/220] md:col-span-2 md:row-span-2 md:aspect-auto md:h-full`
  }

  if (index === 5) {
    return `${base} col-span-2 aspect-[358/160] md:col-span-1 md:aspect-auto md:h-full`
  }

  return `${base} col-span-1 aspect-[173/130] md:aspect-auto md:h-full`
}

function gridImageAlt(
  item: NonNullable<TeaserImageGridBlockType['imageGrid']>[number],
  index: number,
): string {
  const alt = item.image?.alternativeText?.trim()
  if (alt) return alt
  const caption = item.image?.caption?.trim()
  if (caption) return caption
  return `Gallery image ${index + 1}`
}

export default function TeaserImageGridClient({ data }: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const items = data.imageGrid ?? []
  if (items.length === 0) return null

  const configuration = data.configurations?.[0]
  const backgroundColor = configuration?.backgroundColor?.trim() || undefined
  const backgroundPattern =
    configuration?.addBackgroundPattern && configuration.backgroundPattern?.url
      ? getStrapiMedia(configuration.backgroundPattern.url)
      : null
  const displayStyle = (data.displayStyle ?? '').trim().toLowerCase()
  const isCarousel = displayStyle === 'carousel'
  const defaultBg = isCarousel ? ' bg-surface-white' : ' bg-surface-linen'

  const openLightbox = (index: number) => {
    if (!items[index]?.image?.url) return
    setLightboxIndex(index)
  }

  return (
    <section
      data-fig="teaser-image-grid"
      data-display-style={data.displayStyle ?? 'mosaic'}
      className={`relative px-spacing-section-x ${
        isCarousel ? 'py-10 md:pb-10 md:pt-16' : 'py-12 md:py-16'
      }${backgroundColor ? '' : defaultBg}`}
      style={backgroundColor ? { backgroundColor } : undefined}
      aria-label="Photo gallery"
    >
      <BackgroundLayers pattern={backgroundPattern} />

      <div className="relative z-10 mx-auto w-full max-w-width-content">
        {isCarousel ? (
          <TeaserImageGridCarousel items={items} onImageClick={openLightbox} />
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-4 md:aspect-[1200/940] md:grid-cols-3 md:grid-rows-3 md:gap-5">
            {items.map((item, index) => {
              const alt = gridImageAlt(item, index)
              return (
                <FadeUp
                  key={item.id}
                  data-fig={
                    index === 0
                      ? 'teaser-grid-featured'
                      : `teaser-grid-item-${item.id}`
                  }
                  className={getMosaicItemClassName(index)}
                  delay={index * 80}
                  durationMs={1200}
                >
                  <button
                    type="button"
                    onClick={() => openLightbox(index)}
                    className="absolute inset-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset"
                    aria-label={`Open ${alt} in gallery`}
                  >
                    {item.image ? (
                      <Image
                        src={getStrapiMedia(item.image.url)}
                        alt={alt}
                        fill
                        className="object-cover"
                        sizes={
                          index === 0
                            ? '(max-width: 991px) 100vw, 66vw'
                            : '(max-width: 991px) 50vw, 33vw'
                        }
                      />
                    ) : (
                      <div className="h-full w-full bg-surface-white" />
                    )}
                  </button>
                </FadeUp>
              )
            })}
          </div>
        )}
      </div>

      {lightboxIndex != null && (
        <TeaserImageGridLightbox
          items={items}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChangeIndex={setLightboxIndex}
        />
      )}
    </section>
  )
}
